/**
 * Shared app helpers: UUID, URL/query helpers, formatting, email validation, and small browser utilities.
 */

declare global {
    interface Crypto {
        randomUUID(): `${string}-${string}-${string}-${string}-${string}`;
    }
    interface Window {
        Appcues: any;
        AppcuesSettings: any;
    }
}  

export function uuid(): string {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    
    /** fallback to custom uuid implementation */
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        // eslint-disable-next-line
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
    
}

export function createPagedRequestURLSearchParams(
    page: number,
    perPage: number,
    sortBy?: string,
    sortDir?: string
): URLSearchParams {
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('per_page', perPage.toString());

    if (sortBy) {
        searchParams.append('sort_by', sortBy);
    }
    if (sortDir) {
        searchParams.append('sort_dir', sortDir);
    }

    return searchParams;
}

export function decimalFormat(num: number, fractionDigits: number = 2) {
    return num.toFixed(fractionDigits);
}

/**
 * Display-only simplification of a backend reference/trace id, e.g.
 * `RMS_b4f83d21683b49c4a9052df01e62d9ed_1785203949283?merchant_id=273717` becomes
 * `b4f83d21683b49c4a9052df01e62d9ed_1785203949283`.
 *
 * Strips the origin prefix and the `?...` query payload (where real merchant/rma/order data
 * would otherwise leak into the display), but keeps everything else - including underscores
 * and case - untouched, so the result stays an exact substring of the raw id for log search.
 */
export function simplifyReferenceId(id?: string): string | undefined {
    if (!id) {
        return id;
    }

    const withoutQuery = id.split('?')[0];
    const match = withoutQuery.match(/[0-9a-f]{32}.*$/i);
    return match ? match[0] : withoutQuery;
}

export function loadExternalScript(resourceUrl: string) {
    const script = document.createElement('script');
    script.src = resourceUrl;
    script.async = true;

    if (!document.head.querySelector(`script[src="${resourceUrl}"]`)) {
        document.head.appendChild(script);
    }
}

export const getVideoIdFromYTLink = (link: string) => {
    return link.split('=')[1];
}

export const validateEmail = (email: string): boolean => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
        return false;
    }
    
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.\-_]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(trimmedEmail)) {
        return false;
    }
    
    return true;
};

/**
 * Uppercases the first character of each whitespace-separated word and lowercases the rest.
 */
export function capitalizeWords(phrase: string): string {
    return phrase
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
